import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactSwitch from 'react-switch';
import { AdminSettingsSection, Button, Loader, PageBody, SettingsNavigation } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useNotificationSettingsQuery } from 'src/api/queries/notificationSettingsQueries';
import { useUpdateNotificationSettingsMutation } from 'src/api/mutations/notificationSettingsMutations';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { NotificationSettingsUpdatePayload } from 'src/types/TNotificationSettings';
import { FetchError } from 'src/types/TApiQueries';
import useRoleManager from 'src/hooks/useRoleManager';
import DomainChipInput from './components/DomainChipInput';
import messages from './messages';
import styles from './styles.module.scss';

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const extractServerErrorMessage = (err: FetchError | null): string | null => {
  if (!err) return null;
  if (isErrorResponseResult(err)) {
    if (typeof err.error === 'string' && err.error.trim()) return err.error.trim();
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
};

export const NotificationSettings: React.FC = () => {
  const intl = useIntl();
  const { showSuccess, showAlert } = useAlerts();
  const { isAdmin } = useRoleManager();

  const { data, isLoading, error, refetch, isFetching } = useNotificationSettingsQuery(
    { enabled: isAdmin },
  );
  const updateMutation = useUpdateNotificationSettingsMutation();

  const [allowAll, setAllowAll] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setAllowAll(!!data.allow_all_notification_domains);
      setDomains(Array.isArray(data.allowed_notification_domains)
        ? data.allowed_notification_domains
        : []);
      setServerError(null);
    }
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    const savedAllowAll = !!data.allow_all_notification_domains;
    const savedDomains = Array.isArray(data.allowed_notification_domains)
      ? data.allowed_notification_domains
      : [];
    return savedAllowAll !== allowAll || !arraysEqual(savedDomains, domains);
  }, [data, allowAll, domains]);

  if (!isAdmin) {
    return null;
  }

  const handleSave = async () => {
    setServerError(null);
    const payload: NotificationSettingsUpdatePayload = {
      allow_all_notification_domains: allowAll,
      allowed_notification_domains: domains,
    };
    try {
      const result = await updateMutation.mutateAsync(payload);
      showSuccess(intl.formatMessage(messages.savedToast));
      if (result) {
        setAllowAll(!!result.allow_all_notification_domains);
        setDomains(Array.isArray(result.allowed_notification_domains)
          ? result.allowed_notification_domains
          : []);
      }
    } catch (err) {
      const msg = extractServerErrorMessage(err as FetchError);
      if (msg) {
        setServerError(msg);
      } else {
        showAlert(intl.formatMessage(messages.saveError));
      }
    }
  };

  const handleDiscard = () => {
    if (data) {
      setAllowAll(!!data.allow_all_notification_domains);
      setDomains(Array.isArray(data.allowed_notification_domains)
        ? data.allowed_notification_domains
        : []);
      setServerError(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <SettingsNavigation />
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        <SettingsNavigation />
        <div className={styles.errorBanner}>
          <span>
            <FormattedMessage {...messages.loadError} />
          </span>
          <Button mode="secondary" size="small" onClick={() => refetch()} disabled={isFetching}>
            <FormattedMessage {...messages.retry} />
          </Button>
        </div>
      </div>
    );
  }

  const editorDisabled = allowAll || updateMutation.isLoading;

  return (
    <div className={styles.pageWrapper}>
      <SettingsNavigation />

      <PageBody narrow>
        <p className={styles.scopeAlert}>
          <FormattedMessage {...messages.scopeAlert} />
        </p>
        <AdminSettingsSection title={intl.formatMessage(messages.pageTitle)}>
          <p className={styles.sectionDescription}>
            <FormattedMessage {...messages.sectionDescription} />
          </p>

          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>
                  <FormattedMessage {...messages.allowAllLabel} />
                </span>
                <ReactSwitch
                  checked={allowAll}
                  onChange={(checked) => setAllowAll(checked)}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#2e53e3"
                  width={40}
                  height={20}
                  handleDiameter={16}
                  disabled={updateMutation.isLoading}
                />
              </div>
              <span className={styles.fieldHint}>
                <FormattedMessage {...messages.allowAllHint} />
              </span>
              {allowAll && (
                <div className={styles.warningBanner} role="alert">
                  <FormattedMessage {...messages.allowAllWarning} />
                </div>
              )}
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                <FormattedMessage {...messages.domainsLabel} />
              </span>
              <DomainChipInput
                value={domains}
                onChange={setDomains}
                disabled={editorDisabled}
              />
              {allowAll ? (
                <span className={styles.fieldHint}>
                  <FormattedMessage {...messages.domainsDisabledHint} />
                </span>
              ) : domains.length === 0 ? (
                <span className={styles.fieldHint}>
                  <FormattedMessage {...messages.emptyHint} />
                </span>
              ) : null}
            </div>
          </div>
        </AdminSettingsSection>

        {serverError && (
          <div className={styles.serverErrorBanner} role="alert">
            {serverError}
          </div>
        )}

        <div className={styles.actions}>
          <Button
            mode="primary"
            size="small"
            onClick={handleSave}
            disabled={!dirty || updateMutation.isLoading}
          >
            {updateMutation.isLoading ? (
              <FormattedMessage {...messages.saving} />
            ) : (
              <FormattedMessage {...messages.save} />
            )}
          </Button>
          <Button
            mode="tertiary"
            size="small"
            onClick={handleDiscard}
            disabled={!dirty || updateMutation.isLoading}
          >
            <FormattedMessage {...messages.discard} />
          </Button>
        </div>
      </PageBody>
    </div>
  );
};

export default NotificationSettings;
